// ============= INTEL CHAT GATE (layer 1 — DB-driven) =============
// Asks the DB tenant registry whether this tenant's intel chat is enabled, via
// the same RPC the dashboard-agent edge fn uses for its 403 gate
// (fn_intelligence_layer_chat_enabled). One source of truth: onboarding or
// toggling a tenant is a DB row change — no frontend redeploy. Fail-safe: if
// the check errors, the tenant sees the "coming soon" state, never a composer
// whose requests would be refused server-side anyway.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";

export type IntelGate = "pending" | "open" | "coming_soon";

export function useIntelGate(clientId: string | null): IntelGate {
  const { data, isPending } = useQuery({
    queryKey: ["intel-chat-gate", clientId],
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      // The auto-generated Database types predate this RPC (they regenerate
      // from the live schema wholesale), hence the local cast.
      const { data: enabled, error } = (await supabase.rpc(
        "fn_intelligence_layer_chat_enabled" as never,
        { p_project_id: clientId } as never,
      )) as { data: unknown; error: { message: string } | null };
      if (error) throw error;
      return enabled === true;
    },
  });

  if (!clientId || isPending) return "pending";
  // Query error leaves `data` undefined → coming_soon (fail-safe).
  return data ? "open" : "coming_soon";
}
